import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SimpleEvent } from '@passionware/simple-event';
import {
  type AnySpec,
  type KindMap,
  type KindOf,
  readField,
  type SpecOf,
  type UnitValues
} from 'v5/core/form-schema/formKinds.types';
import type { FieldsUnit } from 'v5/core/form-schema/formSchema.types';
import { RenderField } from './RenderField';
import type {
  ClearAction,
  FormKit,
  SchemaFormSize,
  SchemaFormVariant,
  UnitFoldSource,
  UnitTarget
} from './schemaForm.types';
import { useFieldsResolver } from './fieldValidation';
import { SchemaUnitEditor } from './SchemaUnitEditor';
import type { UnitAddAction } from './UnitEditor';
import { NO_UNIT_FOLD } from './unitFold';
import { fieldPath } from './unitPath';

export interface UnitFormProps<M extends KindMap<M>> {
  unit: FieldsUnit<AnySpec<M>>;
  values: UnitValues<M>;
  onSave(values: UnitValues<M>): Promise<void>;
  clear: ClearAction;
  overriddenIn: string[];
  kit: FormKit<M>;
  variant: SchemaFormVariant;
  size: SchemaFormSize;
  /** See `UnitEditorProps.editUnitSignal`. */
  editUnitSignal?: SimpleEvent<UnitTarget>;
  /** Whether the unit folds away (it doesn't when omitted). */
  foldSource?: UnitFoldSource;
  /** See `UnitEditorProps.icon`; `null` for none. */
  icon: ReactNode;
}

/**
 * A unit of fields (`SchemaUnitEditor`): one field component per spec, each value
 * under its path-safe key in the unit's form.
 */
export function UnitForm<M extends KindMap<M>>({
  unit,
  values,
  onSave,
  kit,
  foldSource = NO_UNIT_FOLD,
  ...rest
}: UnitFormProps<M>) {
  const { t } = useTranslation('patterns');
  const fold = foldSource.useFold(unit);
  const resolver = useFieldsResolver<M, UnitValues<M>>(data => {
    const unitValues = fromFormValues(unit, data);
    return unit.fields.map(field => ({
      path: fieldPath('', field.key),
      spec: field,
      values: unitValues
    }));
  }, kit);
  /** A group: its fields show labels of their own under the unit's title. */
  const hasFieldLabels = unit.fields.length > 1;
  const [onlyField] = unit.fields;
  return (
    <SchemaUnitEditor<UnitValues<M>>
      {...rest}
      unit={unit}
      fold={fold ?? undefined}
      // Field keys (`core:emails`) are not RHF-safe; the form stores values
      // under `fieldPath` keys and maps back to spec keys on submit.
      formValues={toFormValues(unit, values)}
      onSave={data => onSave(fromFormValues(unit, data))}
      resolver={resolver}
      required={unit.fields.some(field => field.required)}
      groupTitle={hasFieldLabels}
      isEmpty={data =>
        unit.fields.every(field =>
          isFieldEmpty(kit, field, fromFormValues(unit, data))
        )
      }
      add={
        !hasFieldLabels && onlyField
          ? unitAddAction(kit, onlyField, values, t)
          : undefined
      }
      content={unit.fields.map(field => (
        <RenderField
          key={field.key}
          kit={kit}
          spec={field}
          path={fieldPath('', field.key)}
          hideLabel={!hasFieldLabels}
        />
      ))}
    />
  );
}

/** The header “+” of a unit holding only `spec`, when its kind adds from there. */
function unitAddAction<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>,
  values: UnitValues<M>,
  t: TFunction<'patterns'>
): UnitAddAction | undefined {
  const add = kit[spec.kind].unitAdd;
  if (!add) return undefined;
  return {
    label: add.label(spec, t),
    editHidden: isFieldEmpty(kit, spec, values)
  };
}

function isFieldEmpty<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>,
  values: UnitValues<M>
): boolean {
  return kit[spec.kind].isEmpty(spec, readField<M, K>(values, spec));
}

/** The unit's values under path-safe field keys, as its form holds them. */
function toFormValues<M extends KindMap<M>>(
  unit: FieldsUnit<AnySpec<M>>,
  values: UnitValues<M>
): UnitValues<M> {
  const out: UnitValues<M> = {};
  for (const field of unit.fields) {
    out[fieldPath('', field.key)] = values[field.key];
  }
  return out;
}

function fromFormValues<M extends KindMap<M>>(
  unit: FieldsUnit<AnySpec<M>>,
  data: UnitValues<M>
): UnitValues<M> {
  const out: UnitValues<M> = {};
  for (const field of unit.fields) {
    out[field.key] = data[fieldPath('', field.key)];
  }
  return out;
}
